"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { useLocale } from "next-intl";
import { Link } from "@/navigation";
import { cn } from "@/lib/utils";
import type { AppLocale } from "@/i18n/routing";
interface LocalPricingNudgeProps {
  className?: string;
  currency: string;
  targetLocale: AppLocale;
}
const COUNTRY_PRICING: Record<string, {
  locale: AppLocale;
  currency: string;
}> = {
  DK: {
    locale: "da",
    currency: "DKK"
  },
  DE: {
    locale: "de",
    currency: "EUR"
  },
  GR: {
    locale: "el",
    currency: "EUR"
  },
  ES: {
    locale: "es",
    currency: "EUR"
  },
  FI: {
    locale: "fi",
    currency: "EUR"
  },
  FO: {
    locale: "fo",
    currency: "DKK"
  },
  FR: {
    locale: "fr",
    currency: "EUR"
  },
  IS: {
    locale: "is",
    currency: "ISK"
  },
  IT: {
    locale: "it",
    currency: "EUR"
  },
  NL: {
    locale: "nl",
    currency: "EUR"
  },
  NO: {
    locale: "no",
    currency: "NOK"
  },
  PT: {
    locale: "pt",
    currency: "EUR"
  },
  SE: {
    locale: "sv",
    currency: "SEK"
  }
};
const DISMISS_EVENT = "gg:dismiss-local-pricing-nudge";
const COPY: Record<string, {
  available: string;
  view: string;
  dismiss: string;
}> = {
  da: {
    available: "Lokale priser er tilgængelige.",
    view: "Se priser i",
    dismiss: "Luk forslag om lokale priser"
  },
  de: {
    available: "Lokale Preise sind verfügbar.",
    view: "Preise anzeigen in",
    dismiss: "Hinweis auf lokale Preise schließen"
  },
  el: {
    available: "Υπάρχουν διαθέσιμες τοπικές τιμές.",
    view: "Δείτε τιμές σε",
    dismiss: "Κλείσιμο πρότασης τοπικών τιμών"
  },
  en: {
    available: "Local pricing is available.",
    view: "View prices in",
    dismiss: "Dismiss local pricing suggestion"
  },
  es: {
    available: "Hay precios locales disponibles.",
    view: "Ver precios en",
    dismiss: "Cerrar sugerencia de precios locales"
  },
  fi: {
    available: "Paikalliset hinnat ovat saatavilla.",
    view: "Näytä hinnat valuutassa",
    dismiss: "Sulje paikallisten hintojen ehdotus"
  },
  fo: {
    available: "Lokalir prísir eru tøkir.",
    view: "Sí prísir í",
    dismiss: "Lat uppskotið um lokalar prísir aftur"
  },
  fr: {
    available: "Des tarifs locaux sont disponibles.",
    view: "Voir les prix en",
    dismiss: "Fermer la suggestion de tarifs locaux"
  },
  is: {
    available: "Staðbundið verð er í boði.",
    view: "Sjá verð í",
    dismiss: "Loka tillögu um staðbundið verð"
  },
  it: {
    available: "Sono disponibili prezzi locali.",
    view: "Vedi i prezzi in",
    dismiss: "Chiudi il suggerimento sui prezzi locali"
  },
  nl: {
    available: "Lokale prijzen zijn beschikbaar.",
    view: "Bekijk prijzen in",
    dismiss: "Suggestie voor lokale prijzen sluiten"
  },
  no: {
    available: "Lokale priser er tilgjengelige.",
    view: "Se priser i",
    dismiss: "Lukk forslag om lokale priser"
  },
  pt: {
    available: "Estão disponíveis preços locais.",
    view: "Ver preços em",
    dismiss: "Fechar sugestão de preços locais"
  },
  sv: {
    available: "Lokala priser är tillgängliga.",
    view: "Visa priser i",
    dismiss: "Stäng förslaget om lokala priser"
  }
};
const FLAGS: Record<AppLocale, string> = {
  da: "🇩🇰",
  de: "🇩🇪",
  el: "🇬🇷",
  en: "🌍",
  es: "🇪🇸",
  fi: "🇫🇮",
  fo: "🇫🇴",
  fr: "🇫🇷",
  is: "🇮🇸",
  it: "🇮🇹",
  nl: "🇳🇱",
  no: "🇳🇴",
  pt: "🇵🇹",
  sv: "🇸🇪"
};
export function DetectedLocalPricingNudge(t0) {
  const $ = _c(6);
  const {
    className
  } = t0;
  const locale = useLocale();
  const [localPricing, setLocalPricing] = useState();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const country = document.cookie.match(/(?:^|; )gg-country=([^;]+)/)?.[1];
      setLocalPricing(country ? COUNTRY_PRICING[country] : undefined);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (!localPricing || localPricing.locale === locale) {
    return null;
  }
  let t3;
  if ($[2] !== className || $[3] !== localPricing.currency || $[4] !== localPricing.locale) {
    t3 = <LocalPricingNudge className={className} currency={localPricing.currency} targetLocale={localPricing.locale} />;
    $[2] = className;
    $[3] = localPricing.currency;
    $[4] = localPricing.locale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export default function LocalPricingNudge(t0) {
  const $ = _c(26);
  const {
    className,
    currency,
    targetLocale
  } = t0;
  const locale = useLocale();
  const [isVisible, setIsVisible] = useState(true);
  const copy = COPY[locale] ?? COPY.en;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const dismiss = () => setIsVisible(false);
      window.addEventListener(DISMISS_EVENT, dismiss);
      return () => window.removeEventListener(DISMISS_EVENT, dismiss);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (!isVisible) {
    return null;
  }
  let t3;
  if ($[2] !== targetLocale) {
    t3 = () => {
      document.cookie = `NEXT_LOCALE=${targetLocale}; path=/; max-age=31536000; samesite=lax`;
    };
    $[2] = targetLocale;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const persistLocale = t3;
  const dismiss_0 = _temp;
  const t4 = copy.available;
  let t5;
  if ($[4] !== className) {
    t5 = cn("mx-auto flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-2xl border border-[#e7cfaa] bg-[#fffaf2] px-3 py-1.5 text-xs text-[#69583f] shadow-[0_8px_24px_-18px_rgba(83,56,12,0.5)] sm:rounded-full", className);
    $[4] = className;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = FLAGS[targetLocale];
  let t7;
  if ($[6] !== t6) {
    t7 = <span aria-hidden="true">{t6}</span>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== copy.available) {
    t8 = <span>{copy.available}</span>;
    $[8] = copy.available;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ArrowRight className="h-3 w-3" aria-hidden="true" />;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== copy.view || $[12] !== currency || $[13] !== persistLocale || $[14] !== targetLocale) {
    t10 = <Link href="/" locale={targetLocale} onClick={persistLocale} className="inline-flex items-center gap-1 font-semibold text-[#8a570b] underline decoration-[#d9a34e] underline-offset-2 transition-colors hover:text-primary">{copy.view} {currency}{t9}</Link>;
    $[11] = copy.view;
    $[12] = currency;
    $[13] = persistLocale;
    $[14] = targetLocale;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <X className="h-3.5 w-3.5" aria-hidden="true" />;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== copy.dismiss) {
    t12 = <button type="button" onClick={dismiss_0} className="ml-0.5 rounded-full p-0.5 text-[#8c7a60] transition-colors hover:bg-[#f4e7d2] hover:text-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1" aria-label={copy.dismiss}>{t11}</button>;
    $[17] = copy.dismiss;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== copy.available || $[20] !== t10 || $[21] !== t12 || $[22] !== t5 || $[23] !== t7 || $[24] !== t8) {
    t13 = <aside aria-label={t4} className={t5}>{t7}{t8}{t10}{t12}</aside>;
    $[19] = copy.available;
    $[20] = t10;
    $[21] = t12;
    $[22] = t5;
    $[23] = t7;
    $[24] = t8;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
}
function _temp() {
  return window.dispatchEvent(new Event(DISMISS_EVENT));
}